import React, { useState, useEffect, useRef, useCallback } from 'react';
import Colors from '../utilities/Colors';
import {
  onHoverHistogram,
  onClickHistogram,
  onSetActiveIndicator,
  onSetActiveDBModuleId,
  onSetActiveRGModuleId,
  onSetActiveSTModuleId,
  onSetActiveModuleId,
  useGlobalActivePage
} from '../../data/StatusStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

interface DataItem {
  id: string;
  value: any;
}

export interface HistogramStaticProps {
  dataArray: DataItem[];
  histMax: number;
  histNA: number;
  histLength: number;
  chartId: string;
  height: string;
  width: string;
  paddingLeft: string;
  paddingRight: string;
  colorClass: string;
  DESCRIPTOR: string;
  options?: any;
  nycAvg?: number;
}

export const HistogramStatic: React.FC<HistogramStaticProps> = ({
  dataArray,
  histMax,
  histNA,
  histLength,
  chartId,
  height,
  width,
  paddingRight,
  paddingLeft,
  colorClass,
  DESCRIPTOR,
  options,
  nycAvg
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const lastHoverIdRef = useRef<string | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const activePage = useGlobalActivePage();

  //console.log("A062424 HistogramStatic dataArray", dataArray);
  //console.log("A062424 HistogramStatic chartId", chartId);

  useEffect(() => {
    // Assuming the loading state can be determined by checking if dataArray is populated
    if (dataArray && dataArray.length > 0) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [dataArray]);

  const base = 10;
  const viewBoxWidth = histLength * base;
  const _height = parseFloat(height);

  const bar_width =
    dataArray && dataArray.length > 100
      ? dataArray.length > 1000
        ? base * 2 //10//9.8
        : base
      : base - 1;

  const stroke =
    dataArray && dataArray.length > 100
      ? dataArray.length > 1000
        ? 0
        : -0.2
      : 0;

  /**
   * Convert a clientX to a histogram index using the SVG's rendered width and viewBox width.
   * We attach a single handler to the SVG to avoid per-rect closures/listeners.
   */
  const getIndexFromClientX = useCallback(
    (e: React.PointerEvent<SVGSVGElement> | React.MouseEvent<SVGSVGElement>) => {
      const svg = e.currentTarget;
      const rect = svg.getBoundingClientRect();

      if (!rect.width) return null;

      const xPx = e.clientX - rect.left;
      if (xPx < 0 || xPx > rect.width) return null;

      const pct = xPx / rect.width;
      const vbX = pct * viewBoxWidth;
      const idx = Math.floor(vbX / base);

      if (!dataArray || idx < 0 || idx >= dataArray.length) return null;
      return idx;
    },
    [base, dataArray, viewBoxWidth]
  );

  const handleHoverId = useCallback((id: string | null) => {
    // Avoid spamming store updates if hover id hasn't actually changed
    if (lastHoverIdRef.current === id) return;
    lastHoverIdRef.current = id;
    onHoverHistogram(id);
  }, []);

  const handleHistogramClickById = useCallback(
    (id: string) => {
      onClickHistogram(
        id,
        'histogram',
        options ? options.variables[0].sort.moduleId : 'main_histogram'
      );

      //onSetActiveIndicator(chartId, options.type.toLowerCase(), options.variables[0].sort.moduleId)
      console.log('C112724 options', options);
      console.log('C112724 colorClass', colorClass);

      if (options) {
        if (activePage !== 'dashboard') {
          if (activePage === 'stories') {
            onSetActiveSTModuleId(options.variables[0].sort.moduleId);
          } else if (activePage === 'rg') {
            onSetActiveRGModuleId(options.variables[0].sort.moduleId);
          } else {
            onSetActiveModuleId(options.variables[0].sort.moduleId);
          }
        } else {
          onSetActiveDBModuleId(options.variables[0].sort.moduleId);
        }
      }

      onSetActiveIndicator(
        chartId.replace('_stacked_hist', ''),
        'histogram',
        options ? options.variables[0].sort.moduleId : 'main_histogram'
      );
    },
    [activePage, chartId, colorClass, options]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const idx = getIndexFromClientX(e);
      if (idx == null) {
        handleHoverId(null);
        return;
      }

      const id = dataArray?.[idx]?.id ?? null;
      handleHoverId(id);
    },
    [dataArray, getIndexFromClientX, handleHoverId]
  );

  const handlePointerLeave = useCallback(() => {
    handleHoverId(null);
  }, [handleHoverId]);

  const handleSvgClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const idx = getIndexFromClientX(e);
      if (idx == null) return;

      const id = dataArray?.[idx]?.id;
      if (!id) return;

      handleHistogramClickById(id);
    },
    [dataArray, getIndexFromClientX, handleHistogramClickById]
  );

  // IMPORTANT: return after hooks
  if (!dataArray || dataArray.length === 0) {
    return null;
  }

  return (
    <div
      className="histogram-container"
      style={{
        //paddingLeft: '20px',
      }}
    >
      {isLoading ? (
        <div className="loading-spinner">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      ) : (
        <svg
          id={`${chartId}_hist_static_svg`}
          ref={svgRef}
          width={width}
          height={`${height}px`}
          viewBox={`0 0 ${viewBoxWidth} ${height}`}
          preserveAspectRatio="none"
          className="hist-container"
          style={{
            paddingRight: paddingRight,
            paddingLeft: paddingLeft,
          }}
          onPointerMove={handlePointerMove}
          onPointerLeave={handlePointerLeave}
          onClick={handleSvgClick}
        >
          {dataArray.map((d, i) => {
            const normalizedValue =
              d.value === 'NA' ? histMax / (_height / 2) : d.value;

            const barHeight = (normalizedValue / histMax) * _height;
            const _color = Colors.getColorQuintile(
              i,
              histNA,
              histLength,
              colorClass
            );

            return (
              <rect
                key={`${chartId}_${i}`}
                x={i * base}
                y={_height - barHeight}
                width={bar_width}
                height={barHeight}
                fill={_color}
                stroke={_color}
                strokeWidth={stroke}
                className="hist-bar"
                // NOTE: no per-rect onMouseOver/onClick anymore (event delegation on the SVG)
              />
            );
          })}
        </svg>
      )}
    </div>
  );
};
